import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ADMIN_SCOPES, hasAdminScope } from '@/lib/auth';
import { sendWebsiteChatHeartbeat } from '@/lib/api';
import { HEARTBEAT_MS } from '@/lib/websiteEngagement';

/**
 * Tell the server this staff member is available to website visitors.
 *
 * Mounted once in the app shell rather than on the Website Messages page, so
 * someone working anywhere in the portal still counts as reachable — the old
 * behaviour meant the site showed "offline" unless somebody happened to have
 * the inbox open.
 *
 * Three things this deliberately gets right:
 *  - Only authorized staff beat. A student, instructor or agency would just be
 *    refused by the API, so we never ask.
 *  - One timer per document, guarded by a module-level lock, so two mounts (a
 *    remount, or the shell rendering twice in StrictMode) cannot double up.
 *  - Nothing beats while the tab is hidden. Presence means "someone is here to
 *    answer", and a background tab left open overnight is not that.
 *
 * The server treats a beat as fresh for ~90s, so the 30s interval survives one
 * missed beat before the visitor-facing indicator flips to offline.
 */

// Module scope, not a ref: two hook instances in the same document must resolve
// to one heartbeat, and a ref is per-instance.
let heartbeatOwnerCount = 0;

export function isPresenceEligible(profile) {
  return profile?.role === 'admin' && hasAdminScope(profile, ADMIN_SCOPES.STUDENT);
}

export default function useStaffPresence() {
  const { profile } = useAuth();
  const eligible = isPresenceEligible(profile);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!eligible) return undefined;

    // Another mount already owns the timer for this document.
    heartbeatOwnerCount += 1;
    if (heartbeatOwnerCount > 1) {
      return () => {
        heartbeatOwnerCount -= 1;
      };
    }

    let cancelled = false;

    const beat = () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      sendWebsiteChatHeartbeat().catch((error) => {
        // A failed beat is not worth interrupting anyone over: the server
        // simply lets this person age out of the online count.
        console.error('Staff presence heartbeat failed', error);
      });
    };

    const start = () => {
      if (timerRef.current) return;
      beat();
      timerRef.current = window.setInterval(beat, HEARTBEAT_MS);
    };

    const stop = () => {
      if (!timerRef.current) return;
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      heartbeatOwnerCount -= 1;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stop();
    };
  }, [eligible]);
}
